import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api"; // centralized axios wrapper (get/post/put/patch/delete)

// Doctor-side CuoreCare data layer. All calls hit /api/cuorecare/* on the backend
// (base URL from utils/api — set REACT_APP_API_URL to the staging URL to test).

export const fetchCuoreCareAppointments = createAsyncThunk(
  "cuoreCare/fetchAppointments",
  async ({ scope = "all", search = "" } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/appointments", { scope, search });
      return res.data?.appointments ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load appointments" });
    }
  }
);

// The pending requests on their own, fetched WITHOUT going through
// fetchCuoreCareAppointments: that thunk writes into the console's `appointments` list, and the
// Notifications page only wants to know which bookings are still awaiting a decision — it must
// not overwrite whatever scope the console last loaded.
export const fetchPendingCuoreCareRequests = createAsyncThunk(
  "cuoreCare/fetchPendingRequests",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/appointments", { scope: "requests" });
      return res.data?.appointments ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load pending requests" });
    }
  }
);

export const fetchCuoreCareAppointment = createAsyncThunk(
  "cuoreCare/fetchAppointment",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}`);
      return res.data?.appointment;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load appointment" });
    }
  }
);

/* What the PATIENT owes for this consultation. The doctor is a participant on the appointment,
   so the same endpoint the patient's checkout reads is readable here — which is how the bill
   learns about a walk-in the patient chose to settle at the desk ("deferred"). 404 is a normal
   answer, not a failure: a walk-in the doctor created in their own queue never had a booking
   payment at all. */
export const getCuoreCareAppointmentPayment = createAsyncThunk(
  "cuoreCare/getAppointmentPayment",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/payment`);
      return res.data?.payment || null;
    } catch (err) {
      if (err.response?.status === 404) return null;
      return rejectWithValue(err.response?.data || { message: "Failed to load the payment" });
    }
  }
);

export const acceptCuoreCareAppointment = createAsyncThunk(
  "cuoreCare/accept",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/cuorecare/appointments/${id}/accept`, {});
      return res.data?.appointment;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to accept appointment" });
    }
  }
);

export const cancelCuoreCareAppointment = createAsyncThunk(
  "cuoreCare/cancel",
  async ({ id, reason = "" }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/cuorecare/appointments/${id}/cancel`, { reason });
      return res.data?.appointment;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to cancel appointment" });
    }
  }
);

export const completeCuoreCareAppointment = createAsyncThunk(
  "cuoreCare/complete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/complete`, {});
      return res.data?.appointment;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to complete consultation" });
    }
  }
);

// Returns { appId, roomId, userId, rtcToken, zimToken, rtcTokenTtlSec, mode } for the
// ZEGO web UIKit / Express SDK. The backend re-validates window + participation first.
export const joinCuoreCareConsultation = createAsyncThunk(
  "cuoreCare/join",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/join`, {});
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to join consultation" });
    }
  }
);

export const createCuoreCarePrescription = createAsyncThunk(
  "cuoreCare/createPrescription",
  async ({ id, prescription }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/prescription`, prescription);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to create prescription" });
    }
  }
);

// Re-mint ZEGO tokens for a live session (used on the SDK's tokenWillExpire callback so a
// long consult never drops). Returns { rtcToken, zimToken, rtcTokenTtlSec }.
export const refreshCuoreCareToken = createAsyncThunk(
  "cuoreCare/refreshToken",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/token/refresh`, {});
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to refresh session token" });
    }
  }
);

// Presence beacon — { type: "joined" | "left" }. Best-effort; the backend also stamps
// presence on join, so a failure here is non-fatal to the call.
export const recordCuoreCareEvent = createAsyncThunk(
  "cuoreCare/recordEvent",
  async ({ id, type }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/events`, { type });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to record event" });
    }
  }
);

// Doctor's issued prescriptions (metadata only). Returns an array.
export const fetchCuoreCarePrescriptions = createAsyncThunk(
  "cuoreCare/fetchPrescriptions",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/prescriptions");
      return res.data?.prescriptions ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load prescriptions" });
    }
  }
);

// Short-lived signed URL to view/download a prescription PDF (PHI — never a public URL).
// Returns { url, prescriptionId, issuedAt, expiresInSec }.
export const getCuoreCarePrescriptionUrl = createAsyncThunk(
  "cuoreCare/getPrescriptionUrl",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/prescriptions/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to open prescription" });
    }
  }
);

// The medicine/follow-up reminders this prescription pushed into the patient's app, with
// their current accept/decline/pending status — polled on demand, see prescription.controller.js
// getReminders.
export const getCuoreCarePrescriptionReminders = createAsyncThunk(
  "cuoreCare/getPrescriptionReminders",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/prescriptions/${id}/reminders`);
      return res.data?.reminders ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load reminder status" });
    }
  }
);

/* The whole diary for one day — every slot, free or booked. Scoped server-side to the caller's
 * own doctor, or, for a reception token, the one doctor it was provisioned against. */
export const getDaySchedule = createAsyncThunk(
  "cuoreCare/getDaySchedule",
  async (params, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/schedule/day", params || {});
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Could not load the day's schedule" });
    }
  }
);

/* The languages a prescription can be printed in, and the advice phrases that have translations.
 * Both come from the server's phrase table (core/clinical/prescriptionLocale.js) rather than a
 * copy in the portal — a language the table cannot actually translate must never be offered. */
export const getPrescriptionLocaleOptions = createAsyncThunk(
  "cuoreCare/getPrescriptionLocaleOptions",
  async (lang, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/web/emr/prescription/locale-options", lang ? { lang } : {});
      return { languages: res.data?.languages ?? [], advicePhrases: res.data?.advicePhrases ?? [] };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load languages" });
    }
  }
);

/* ------------------------------ Casesheet (EMR) ----------------------------- *
 * The structured clinical record for a consultation. Autosaved as a draft during the
 * call, then finalised — which locks it and releases it to the patient.
 * ---------------------------------------------------------------------------- */

// Returns { casesheet } — null when the doctor hasn't started one yet.
export const getCuoreCareCasesheet = createAsyncThunk(
  "cuoreCare/getCasesheet",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/casesheet`);
      // Return the WHOLE payload, not just `.casesheet`: the response also carries
      // `carryForward` (the doctor's own last casesheet), `appPrefill` (the patient's own
      // standing history, resolved newest-wins against any clinician entry — see
      // utils/onboardingHistory.js standingHistory) and the specialty block/profile, all of
      // which the panel seeds from. This used to hand-pick just `casesheet` and `carryForward`
      // into the returned object, which silently dropped `appPrefill` on every load — the
      // "patient's own answer can outrank a stale clinician entry" half of the handshake was
      // computed correctly by the server and then discarded here before it ever reached the
      // panel, so `carryForward` won unconditionally regardless of which side was newer.
      return { ...res.data, casesheet: res.data?.casesheet ?? null, carryForward: res.data?.carryForward ?? null };
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load the casesheet" });
    }
  }
);

// Upsert autosave. Sends the whole casesheet; returns { status, revision, updatedAt }.
export const saveCuoreCareCasesheet = createAsyncThunk(
  "cuoreCare/saveCasesheet",
  async ({ id, casesheet }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/cuorecare/appointments/${id}/casesheet`, casesheet);
      return res.data?.casesheet;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to save the casesheet" });
    }
  }
);

export const finalizeCuoreCareCasesheet = createAsyncThunk(
  "cuoreCare/finalizeCasesheet",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/casesheet/finalize`, {});
      return res.data?.casesheet;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to finalise the casesheet" });
    }
  }
);

// Short-lived signed URL for the finalized casesheet's PDF (PHI — never a public URL).
// Same pattern as getCuoreCarePrescriptionUrl; 409 while still a draft.
// Correct an already-FINALISED casesheet. Distinct from save() on purpose: the backend
// preserves the superseded version, records who/when/why/what-changed, bumps the revision
// and regenerates the PDF. A reason is mandatory.
export const amendCuoreCareCasesheet = createAsyncThunk(
  "cuoreCare/amendCasesheet",
  async ({ id, casesheet, reason }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/casesheet/amend`, { ...casesheet, reason });
      return res.data?.casesheet;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to amend the casesheet" });
    }
  }
);

// The correction history: who changed what, when and why.
export const getCuoreCareCasesheetAmendments = createAsyncThunk(
  "cuoreCare/getCasesheetAmendments",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/casesheet/amendments`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load the amendment history" });
    }
  }
);

export const getCuoreCareCasesheetPdfUrl = createAsyncThunk(
  "cuoreCare/getCasesheetPdfUrl",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/casesheet/pdf`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to open the casesheet PDF" });
    }
  }
);

/* ------------------------------- Certificates ------------------------------- */

export const createCuoreCareCertificate = createAsyncThunk(
  "cuoreCare/createCertificate",
  async ({ id, certificate }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/cuorecare/appointments/${id}/certificate`, certificate);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to issue the certificate" });
    }
  }
);

export const fetchCuoreCareCertificates = createAsyncThunk(
  "cuoreCare/fetchCertificates",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/certificates");
      return res.data?.certificates ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load certificates" });
    }
  }
);

// Short-lived signed URL for a certificate PDF (PHI — never a public URL).
export const getCuoreCareCertificateUrl = createAsyncThunk(
  "cuoreCare/getCertificateUrl",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/certificates/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to open the certificate" });
    }
  }
);

/* ---------------------------- Clinical templates ---------------------------- */

export const fetchCuoreCareTemplates = createAsyncThunk(
  "cuoreCare/fetchTemplates",
  async (kind, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/templates", kind ? { kind } : {});
      return res.data?.templates ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load templates" });
    }
  }
);

// "Save as template" — saving under an existing name for the same kind overwrites it.
export const saveCuoreCareTemplate = createAsyncThunk(
  "cuoreCare/saveTemplate",
  async ({ name, kind, content }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/cuorecare/templates", { name, kind, content });
      return res.data?.template;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to save the template" });
    }
  }
);

export const deleteCuoreCareTemplate = createAsyncThunk(
  "cuoreCare/deleteTemplate",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/cuorecare/templates/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to delete the template" });
    }
  }
);

// Fire-and-forget usage counter so the picker can surface go-to templates first.
export const markCuoreCareTemplateUsed = createAsyncThunk(
  "cuoreCare/markTemplateUsed",
  async (id, { rejectWithValue }) => {
    try {
      await api.post(`/api/cuorecare/templates/${id}/use`, {});
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to record template use" });
    }
  }
);

/* --------------------- Patient clinical context (doctor) -------------------- */

// Everything the doctor should see while consulting: demographics, a vitals trend
// (merged from the patient's Cuore app readings and past casesheets), and their history.
export const getCuoreCarePatientSummary = createAsyncThunk(
  "cuoreCare/getPatientSummary",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/patient-summary`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load the patient summary" });
    }
  }
);

// Rendered fresh on every call (the Chart tab is a live view, not a one-time "issued"
// document) — returns a PDF Blob directly rather than a stored/signed URL.
export const getCuoreCarePatientSummaryPdf = createAsyncThunk(
  "cuoreCare/getPatientSummaryPdf",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/appointments/${id}/patient-summary/pdf`, {}, { responseType: "blob" });
      return res.data;
    } catch (err) {
      return rejectWithValue({ message: "Failed to generate the patient summary PDF" });
    }
  }
);

// Backs "Repeat" — the doctor's last prescription for this patient, ready to prefill.
export const getCuoreCareLastPrescription = createAsyncThunk(
  "cuoreCare/getLastPrescription",
  async (patientId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/cuorecare/patients/${patientId}/last-prescription`);
      return res.data?.prescription ?? null;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load the previous prescription" });
    }
  }
);

// Doctor's weekly availability template. Returns { configured, slotDurations, availability }.
// The backend derives the doctor from the JWT, so no id is sent.
export const getCuoreCareAvailability = createAsyncThunk(
  "cuoreCare/getAvailability",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/availability");
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load availability" });
    }
  }
);

// Upsert the doctor's availability template. Returns the saved availability document.
export const saveCuoreCareAvailability = createAsyncThunk(
  "cuoreCare/saveAvailability",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.put("/api/cuorecare/availability", payload);
      return res.data?.availability;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to save availability" });
    }
  }
);

// The patient's consultation history with the signed-in doctor, newest first, for the
// patient record page. Cursor-paginated: pass `before` (the previous page's nextCursor)
// to load older visits. A 403/503 here means the doctor is not on CuoreCare or the
// feature is off — the caller hides the section rather than showing an error.
export const fetchCuoreCarePatientHistory = createAsyncThunk(
  "cuoreCare/patientHistory",
  async ({ patientId, before = null, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const params = { limit };
      if (before) params.before = before;
      const res = await api.get(`/api/cuorecare/patients/${patientId}/history`, params);
      return res.data;
    } catch (err) {
      return rejectWithValue({
        status: err.response?.status,
        ...(err.response?.data || { message: "Failed to load consultation history" }),
      });
    }
  }
);

/* ------------------------- Clinical suggestions (cascade) ------------------------ *
 * Backs the guided prescription flow used when no template is applied. Two calls only:
 * search the diagnosis vocabulary, then pull one bundle per diagnosis change. Symptom
 * ticks filter the bundle client-side, so they cost nothing.
 * -------------------------------------------------------------------------------- */

// Diagnosis picker. Empty `q` returns the most-used diagnoses.
export const searchCuoreCareDiagnoses = createAsyncThunk(
  "cuoreCare/searchDiagnoses",
  async ({ q = "", limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/suggestions/diagnoses", { q, limit });
      return res.data?.diagnoses ?? [];
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load diagnoses" });
    }
  }
);

// Symptoms / medicines / advice for the selected diagnoses. Names are pipe-joined
// because a diagnosis may legitimately contain a comma ("Diabetes, uncontrolled").
export const fetchCuoreCareSuggestionBundle = createAsyncThunk(
  "cuoreCare/suggestionBundle",
  async (diagnosisNames = [], { rejectWithValue }) => {
    try {
      const names = (Array.isArray(diagnosisNames) ? diagnosisNames : [diagnosisNames]).filter(Boolean);
      if (!names.length) return { symptoms: [], medicines: [], advice: [], matched: [], unmatched: [], reviewed: false };
      const res = await api.get("/api/cuorecare/suggestions/bundle", { diagnoses: names.join("|") });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: "Failed to load suggestions" });
    }
  }
);

/* ── Teleconsult consent (spec §11.3) ────────────────────────────────────────────────────────
 * The doctor must accept the Cuore Teleconsultation Terms and the applicable Telemedicine
 * Practice Guidelines before they can publish teleconsult availability or join a teleconsult.
 * Both server-side gates return 403 TELECONSULT_CONSENT_REQUIRED with the same `consent` shape
 * this endpoint returns, so a blocked action can render the checkboxes without a second request.
 * ---------------------------------------------------------------------------------------- */
export const getTeleconsultConsentThunk = createAsyncThunk(
  "cuoreCare/teleconsultConsent/get",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/cuorecare/teleconsult-consent");
      if (res.status === 200) return res.data.consent;
      return rejectWithValue({ message: res.data?.message || "Could not load consent status" });
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: err.message || "Could not load consent status" }
      );
    }
  }
);

export const acceptTeleconsultConsentThunk = createAsyncThunk(
  "cuoreCare/teleconsultConsent/accept",
  async (_, { rejectWithValue }) => {
    try {
      // Both flags are always sent true together: the server refuses a partial acceptance with
      // 400 CONSENT_INCOMPLETE, because half a consent is a state no product flow wants.
      const res = await api.post("/api/cuorecare/teleconsult-consent", {
        terms: true,
        guidelines: true,
      });
      if (res.status === 200) return res.data.consent;
      return rejectWithValue({ message: res.data?.message || "Could not record acceptance" });
    } catch (err) {
      return rejectWithValue(
        err.response?.data || { message: err.message || "Could not record acceptance" }
      );
    }
  }
);
